import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiService from '../services/api';

const POLL_INTERVAL_MS = 60_000;

type IPoEStatus = {
  status: 'online' | 'offline';
  cgnat_ip: string | null;
  upload_bytes: number | null;
  download_bytes: number | null;
  hostname: string | null;
  last_seen: string | null;
};

function formatBytesTraffic(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function StatusDot({ online }: { online: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: online ? '#1a7f4b' : '#64748b',
        mr: 1,
        flexShrink: 0,
      }}
    />
  );
}

function StatField({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Grid>
  );
}

type Props = {
  macAddress: string;
  assignedIp?: string | null;
};

export default function SupaIPoEStatus({ macAddress, assignedIp }: Props) {
  const [data, setData] = useState<IPoEStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await apiService.adminGetIPoEStatus(macAddress, assignedIp);
      setData(result);
      setError(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Failed to fetch IPoE status');
    } finally {
      setLoading(false);
      setLastChecked(new Date());
    }
  }, [macAddress, assignedIp]);

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStatus();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
  };

  const isOnline = data?.status === 'online';

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <SectionLabel>IPoE connection status</SectionLabel>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isOnline ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <CircularProgress size={14} sx={{ mr: 1 }} />
          ) : (
            <StatusDot online={isOnline} />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: isOnline ? '#1a7f4b' : '#64748b' }}
          >
            {loading ? 'Checking…' : error ? 'Error' : isOnline ? 'Online' : 'Offline'}
          </Typography>
          {error && (
            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastChecked && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastChecked.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: isOnline && data ? 2 : 0 }}>
        <StatField label="MAC Address" value={macAddress} />
      </Grid>

      {isOnline && data && (
        <Grid container spacing={2}>
          {data.cgnat_ip && (
            <StatField label="CGNAT IP" value={data.cgnat_ip} />
          )}
          {data.hostname && (
            <StatField label="Hostname" value={data.hostname} />
          )}
          {data.upload_bytes != null && (
            <StatField label="Upload (session)" value={formatBytesTraffic(data.upload_bytes)} />
          )}
          {data.download_bytes != null && (
            <StatField label="Download (session)" value={formatBytesTraffic(data.download_bytes)} />
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Traffic counters reset each DHCP lease renewal (~24h). This shows current lease period only.
            </Typography>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}
